import { HttpException, viewerHttp } from '../http';

/**
 * The five live study-management endpoints (§5.2). A module of FUNCTIONS, not a class:
 * with the base URL derived per call there is no state to hold, and develop's two
 * clients both captured getViewerUrl() in their constructors — the same freezing bug
 * PACSApiClient had (parent §9.2).
 *
 * A sixth endpoint exists on develop — report-assessment-api-client's own revoke,
 * posting {} — with zero callers. Not replayed. The live revoke carries
 * { studyInstanceUid }.
 *
 * Endpoints carry no leading slash, per M2's convention; HttpClient.buildUrl joins.
 */

export enum DiagnosisStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  NOT_FOUND = 'NOT_FOUND',
}

/** One row as the server sends it. Every field is optional; see the stub's README. */
export interface FindingAssessment {
  findingId?: string | null;
  name?: string;
  isClassificationCorrect?: boolean;
  isDetectionCorrect?: boolean;
}

/**
 * One row as we send it back. The no-findings row carries `findingId: null` and NO
 * `name`, while a finding row carries both — an asymmetry inherited from develop
 * (prepareAssessmentsForSubmission:303-308 vs getUnsubmittedAssessments:200-205) and
 * encoded in the stub.
 */
export interface AssessmentSubmission {
  findingId: string | null;
  name?: string;
  isClassificationCorrect?: boolean;
  isDetectionCorrect?: boolean;
}

interface DiagnosisStatusResponse {
  status?: DiagnosisStatus;
}

interface ReportAssessmentsResponse {
  reportId?: string;
  findingAssessments?: FindingAssessment[];
}

const studyPath = (studyInstanceUid: string) => `studies/${encodeURIComponent(studyInstanceUid)}`;

/**
 * A body with no `status` falls to NOT_FOUND, which routes through the completed path
 * and resolves to IDLE because no report exists (§5.8). develop broadcast a
 * DIAGNOSIS_NOT_FOUND event here that nothing listened to; the routing is what is kept.
 */
export async function fetchDiagnosisStatus(studyInstanceUid: string): Promise<DiagnosisStatus> {
  const { data } = await viewerHttp.get<DiagnosisStatusResponse>('diagnoses/status', {
    params: { studyInstanceUid },
  });

  return data?.status ?? DiagnosisStatus.NOT_FOUND;
}

export async function triggerDiagnosis(studyInstanceUid: string): Promise<void> {
  await viewerHttp.post('diagnoses/trigger', { studyInstanceUid });
}

export async function revokeReport(studyInstanceUid: string): Promise<void> {
  await viewerHttp.post(`${studyPath(studyInstanceUid)}/reports/revoke`, { studyInstanceUid });
}

/**
 * Errors are NOT swallowed (plan D-9). develop returned { findingAssessments: [] } on
 * any failure, turning a 500 into "this study has no report" and dropping the button
 * to IDLE. DiagnosisService.refresh() catches once and leaves the state alone instead.
 */
export async function fetchReportAssessments(
  studyInstanceUid: string
): Promise<{ reportId?: string; findingAssessments: FindingAssessment[] }> {
  const { data } = await viewerHttp.get<ReportAssessmentsResponse>(
    `${studyPath(studyInstanceUid)}/reports/assessments`
  );

  return { reportId: data?.reportId, findingAssessments: data?.findingAssessments ?? [] };
}

export async function submitReportAssessments(
  studyInstanceUid: string,
  assessments: AssessmentSubmission[]
): Promise<void> {
  await viewerHttp.post(`${studyPath(studyInstanceUid)}/reports/assess`, { assessments });
}

export enum OrganEnum {
  PANCREAS = 'PANCREAS',
  KIDNEY = 'KIDNEY',
  LIVER = 'LIVER',
}

export enum OrganQualityEnum {
  GOOD = 'GOOD',
  POOR = 'POOR',
}

export enum OrganReviewStatusEnum {
  IN_PROGRESS = 'IN_PROGRESS',
  NOT_FOUND = 'NOT_FOUND',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

/**
 * The single organ list. develop had two that could drift —
 * OrganReviewDialog.tsx:32's Object.values(OrganEnum) and
 * useOrganReviewStore.ts:161's hardcoded [PANCREAS, KIDNEY, LIVER].
 */
export const ORGANS: readonly OrganEnum[] = Object.values(OrganEnum);

export const ORGAN_DISPLAY_NAMES: Record<OrganEnum, string> = {
  [OrganEnum.PANCREAS]: 'Pancreas',
  [OrganEnum.KIDNEY]: 'Kidney',
  [OrganEnum.LIVER]: 'Liver',
};

/**
 * D9. The Orthanc label vocabulary, keyed to the enum so a fourth organ fails to COMPILE
 * until its label is supplied -- the discipline OrganReviewDialog.tsx:34's ROW_STATES uses.
 *
 * develop hardcoded ['QAPancreas','QAKidney','QALiver'] at
 * useSeriesOrganReviewsOverlayStore.ts:124, which would have been a THIRD organ list beside
 * the two :117-119 above records develop having.
 *
 * NOT derived from ORGAN_DISPLAY_NAMES as `QA` + display name, even though the strings
 * coincide today: OrganEnum.PANCREAS is 'PANCREAS', so the derivation would have to route
 * through the DISPLAY name -- and M5 translates display names to Spanish, which would
 * silently rewrite a wire value.
 */
export const ORGAN_QA_LABELS: Record<OrganEnum, string> = {
  [OrganEnum.PANCREAS]: 'QAPancreas',
  [OrganEnum.KIDNEY]: 'QAKidney',
  [OrganEnum.LIVER]: 'QALiver',
};

export interface OrganReview {
  reviewId: string;
  organ: OrganEnum;
  status?: OrganReviewStatusEnum;
  quality?: OrganQualityEnum;
}

export interface OrganReviewSubmission {
  reviewId: string;
  quality: OrganQualityEnum;
}

const ORGAN_VALUES = new Set<string>(ORGANS);
const QUALITY_VALUES = new Set<string>(Object.values(OrganQualityEnum));
const STATUS_VALUES = new Set<string>(Object.values(OrganReviewStatusEnum));

/**
 * §5.6 consequence 2. develop wrote `response.data as OrganReview[]`
 * (OrganReviewApiClient.ts:24) over a wire type whose `status` and `organ` are plain
 * `string` (OrganReviewTypes.ts:36-41), so backend enum drift flowed silently into the
 * map. An `as` assertion is legal under any compiler flag, so the typecheck gate cannot
 * catch it; only not writing one helps.
 *
 * A row that fails this is DROPPED, not defaulted: without a usable reviewId or organ
 * there is nothing to submit against, and the organ then renders as D5's unissued state,
 * which is modelled. Absent status/quality are legal — an ungraded organ has neither, and
 * develop normalised both spellings of absent at useOrganReviewStore.ts:131-135.
 */
function toOrganReview(row: unknown): OrganReview | null {
  if (!row || typeof row !== 'object') {
    return null;
  }

  const { reviewId, organ, status, quality } = row as Record<string, unknown>;

  if (typeof reviewId !== 'string' || !reviewId) {
    return null;
  }

  if (typeof organ !== 'string' || !ORGAN_VALUES.has(organ)) {
    return null;
  }

  return {
    reviewId,
    organ: organ as OrganEnum,
    status:
      typeof status === 'string' && STATUS_VALUES.has(status)
        ? (status as OrganReviewStatusEnum)
        : undefined,
    quality:
      typeof quality === 'string' && QUALITY_VALUES.has(quality)
        ? (quality as OrganQualityEnum)
        : undefined,
  };
}

/**
 * The query parameter is `seriesInstanceUID` — capital UID, unlike every other endpoint
 * in this file, which sends `studyInstanceUid`. That asymmetry is the wire contract as
 * develop sends it (OrganReviewApiClient.ts:18-20) and §7.2 checks it against the real
 * host, so it is not tidied.
 *
 * Passed through `params` rather than concatenated into the endpoint, which is what
 * develop did — bypassing HttpClient's own params path and skipping encodeURIComponent.
 *
 * Transport errors are NOT swallowed (D8). develop returned [] on any failure
 * (OrganReviewApiClient.ts:25-28), making "the server 500'd" indistinguishable from
 * "nothing reviewed yet". A body that is not an array throws for the same reason.
 */
export async function fetchOrganReviews(seriesInstanceUid: string): Promise<OrganReview[]> {
  const { data, status } = await viewerHttp.get<unknown>('organ-reviews', {
    params: { seriesInstanceUID: seriesInstanceUid },
  });

  // `status`, not 0. exceptions.ts:12-17 reserves 0 for "no response at all", and
  // HttpClient.ts:46-54 records that HttpResponse.status exists precisely so a caller
  // rejecting a 2xx body need not invent one. SessionService.ts:150-161 is the precedent.
  if (!Array.isArray(data)) {
    throw new HttpException(status, `organ-reviews answered ${status} with a body that is not an array`);
  }

  const reviews = data.map(toOrganReview).filter((review): review is OrganReview => review !== null);

  if (reviews.length !== data.length) {
    console.error(
      `[sycai] organ-reviews returned ${data.length - reviews.length} unusable row(s); ` +
        `they are treated as not yet issued`
    );
  }

  return reviews;
}

export async function submitOrganReviews(
  seriesInstanceUid: string,
  reviews: OrganReviewSubmission[]
): Promise<void> {
  await viewerHttp.post('organ-reviews', { seriesInstanceUID: seriesInstanceUid, reviews });
}

export interface UploadOptions {
  /** D3 + §5.4. Set only by the Continue path, never on a first attempt. */
  force?: boolean;
  signal?: AbortSignal;
  onProgress?: (loaded: number, total: number) => void;
}

interface UploadResponse {
  id?: { studyInstanceUID?: string };
}

/** develop's budget (DicomUploader.ts:32). Not reachable locally — §7.2. */
const UPLOAD_TIMEOUT_MS = 60000 * 5;

/**
 * Resolves to the studyInstanceUID, which is what makes rollback possible (§5.4)
 * and what D10's mitigation would confirm through QIDO before closing the modal.
 *
 * EVERY assumption this function makes about the backend is enumerated in
 * `.deploy/local/stub/README.md`'s "The contract, claim by claim" section, under
 * `POST /dicom-web/studies` — the multipart field name `file`, the
 * `data.id.studyInstanceUID` response shape, `{ message }` error bodies, and the
 * exact spelling `x-skip-validation: 'true'`. Read it before changing any of them:
 * they are inherited from what develop actually shipped
 * (`git show develop:extensions/cornerstone/src/utils/uploader/core/DicomUploader.ts`),
 * not invented here, but no real backend has ever been observed answering them.
 *
 * The DICM check deliberately does NOT live here (§5.2): magic-check-and-preamble
 * is file PREPARATION, and putting it in the client would have the client mutate
 * its argument. uploadBatch calls ensurePreamble per file, immediately before this.
 */
export async function uploadDicomFile(file: File, options: UploadOptions = {}): Promise<string> {
  const body = new FormData();
  body.append('file', file);

  const { data, status } = await viewerHttp.post<UploadResponse>('dicom-web/studies', body, {
    timeout: UPLOAD_TIMEOUT_MS,
    signal: options.signal,
    onUploadProgress: options.onProgress,

    /**
     * Plan D-7. Sent ONLY when forcing, never as the string 'false'. §5.2 fixes
     * develop's boolean-into-a-Record<string,string> (§6 defect 11), but develop
     * also sent the header on every upload — and a backend that tests PRESENCE,
     * `if (req.headers['x-skip-validation'])`, reads 'false' as truthy and skips
     * validation on every upload. That is the exact inverse of the intent, it is
     * silent, and the real backend's parsing is unobserved.
     */
    headers: options.force ? { 'x-skip-validation': 'true' } : undefined,
  });

  const studyInstanceUID = data?.id?.studyInstanceUID;

  // Throws rather than returning '': without a UID there is nothing to roll back,
  // so a silent empty string would make Stop a no-op that reports success.
  // `status`, not 0 — exceptions.ts:12-17 reserves 0 for "no response at all".
  if (typeof studyInstanceUID !== 'string' || !studyInstanceUID) {
    throw new HttpException(status, `upload answered ${status} with no id.studyInstanceUID`);
  }

  return studyInstanceUID;
}

/**
 * §4 "Out": M5's study-list delete column will reuse this — do not add a parallel
 * removeStudy. `delete` leads the name because here it IS the domain verb, and the
 * shape matches its siblings: name the resource, pass the ID.
 *
 * Plan D-13: percent-encoded, like studyPath above. develop interpolated the UID
 * raw (useDicomUpload.ts:235).
 */
export async function deleteStudy(studyInstanceUid: string): Promise<void> {
  await viewerHttp.delete(`dicom-web/studies/${encodeURIComponent(studyInstanceUid)}`);
}
